<script>
  import { onDestroy, onMount, tick } from "svelte";
  import enterView from "enter-view";
  import stickyBits from "stickybits";
  import sanitizeHtml from "sanitize-html";
  import marked from "marked";

  import textdata from "../data/text.json";
  import mq from "../stores/mq.js"; // here, defined laptop = 992px
  import scrollY from "../stores/scrollY.js";
  import {
    currentStep,
    currentProgress,
    scrollDirection,
    reachedScrollBottom,
    currentSubStep
  } from "../stores/scrollVars.js";

  import Scene from "./Scene.svelte";
  import ProgressBar from "./ProgressBar.svelte";
  import Intro from "./Intro.svelte";
  import BounceArrow from "./BounceArrow.svelte";
  import OutroText from "./OutroText.svelte";

  import { select, selectAll } from "d3-selection";
  import { interrupt } from "d3-transition";
  import { text } from "svelte/internal";

  const breakpoints = {
    minHeight: 500,
    maxHeight: 800
  };

  // Options for sanitize-html
  const sanitizeOptions = {
    allowedTags: ["b", "i", "em", "strong", "a", "br", "p", "span", "ul", "ol", "li"],
    allowedAttributes: {
      a: ["href", "target"],
      span: ["class"]
    }
  };

  // Set up dimensions, margins, variables...
  let windowInnerWidth, windowInnerHeight;
  let windowScrollY = 0;
  let figureWidth, figureHeight;

  let scrollTopButtonClicked = false;

  let sceneNamesList, sceneAnnotationsList, currentSceneName, previousSceneName, currentAnnotations, steps;

  document.documentElement.style.setProperty("--minHeight", `${breakpoints.minHeight}px`);
  document.documentElement.style.setProperty("--maxHeight", `${breakpoints.maxHeight}px`);

  $: {
    let vh = windowInnerHeight * 0.01;
    let space = windowInnerHeight - breakpoints.maxHeight;
    // document.documentElement.style.setProperty("--vh", `${vh}px`);
    document.documentElement.style.setProperty("--firstMarginTop", `${space > 25 * vh ? space : 25 * vh}px`);
  }

  function setupStickyfill() {
    stickyBits("#infra-story figure");
  }

  function updateOnEnter(el) {
    el.classList.add("entered");
  }

  function updateOnExit(el) {
    el.classList.remove("entered");
  }

  const getRelevantSteps = (steps, mobile) => steps // steps ? mobile ? steps : steps.filter(el => !el.classList.contains("mobile-annotation")) : [];

  function updateSceneParams(el) {
    let stepMinusOne = Math.max(0, steps.indexOf(el) - 1);
    let previousEl = steps[stepMinusOne];
    previousSceneName = previousEl.getAttribute("scene");
    currentSceneName = el.getAttribute("scene");

    currentAnnotations = sceneAnnotationsList.find(d => d.name == currentSceneName).annotations;
  }

  onMount(async () => {
    setupStickyfill();
    const scrolly = select('infra-story')

    // Extract relevant data
    sceneNamesList = textdata && "story" in textdata ? textdata.story.map((d) => d.scene) : [];
    sceneAnnotationsList =
      textdata && "story" in textdata ? textdata.story.map((d) => ({name: d.scene, annotations: d.annotations ? d.annotations : []})) : [];
    // console.log(sceneAnnotationsList)
    // console.log(textdata)
    // Set up first scene
    const stepClass = "step"; 
    steps = [...document.getElementsByClassName(stepClass)];
    updateSceneParams(steps[0]);

    // Set up enter-view.js
    await tick();
    enterView({
      selector: "." + stepClass, //".scrolly article#narrative .step"
      enter: function (el) {
        scrollTopButtonClicked = false;
        // (scrolling down)
        scrolly.selectAll('*').interrupt()
        updateOnEnter(el);
          $currentStep = +el.getAttribute("step-index"); // get new step (index 0)
          updateSceneParams(el);
        // }
      },
      exit: function (exitEl) {
        scrolly.selectAll('*').interrupt()
        if (!scrollTopButtonClicked){
        // (scrolling up)
        updateOnExit(exitEl);
        $currentProgress = 1; // start at 1 when going up to keep continuity
        $currentStep = Math.max(0, +exitEl.getAttribute("step-index") - 1);
        let el = steps[$currentStep]; //steps[steps.indexOf(exitEl) - 1];
        updateSceneParams(el);
        }
      },
      progress: function (el, progress) {
        if (progress === $currentProgress) return;
        $scrollDirection = progress > $currentProgress ? "down" : "up";
        $currentProgress = progress;
        if (el.classList.contains("lastStep")) {
          $reachedScrollBottom = progress > 0.9 ? true : false;
        }
      },
      offset: 0.1
    });
  });

  function formatText(text) {
    return text ? sanitizeHtml(marked(text), sanitizeOptions) : "";
  }

  if (history.scrollRestoration) {
    history.scrollRestoration = "manual";
  } else {
    window.onbeforeunload = function () {
      windowScrollY = 0;
    };
  }

  function scrollToTop() {
    scrollTopButtonClicked = true;
    $currentStep = 0;
    $currentSubStep = 0;
    $currentProgress = 0;
    $scrollDirection = "down"
    $reachedScrollBottom = false;
    updateSceneParams(steps[0]);
  } 

</script>

<svelte:window bind:innerWidth={windowInnerWidth} bind:innerHeight={windowInnerHeight} bind:scrollY={windowScrollY} />
<a name="top"></a>
<div id="infra-story">
  <!-- <ProgressBar progress={$scrollY / (document.body.scrollHeight - windowInnerHeight)} /> -->
  <section class="scrolly {$mq.classNames}">
    <div class="outro-text" class:active={steps ? $currentStep == steps.length - 1 : 0}>
      <OutroText on:goToTop="{() => scrollToTop()}" links={textdata.socialMedia}/>
        <div class="voila-credits">
          {@html formatText(textdata.credit)}
        </div>
    </div>

    <figure bind:clientWidth={figureWidth} bind:clientHeight={figureHeight} class={$mq.classNames}>
      <Scene sceneName={currentSceneName} annotationsList={currentAnnotations} {previousSceneName} mainAltText={textdata.mainAltText}/>
      <!-- <div id="debugger">
        Scene: {currentSceneName} <br />
        Step: {$currentStep}, Substep: {$currentSubStep} <br />
        Previous scene: {previousSceneName} <br />
        Progress: {$currentProgress} <br />
        Reached Bottom : {$reachedScrollBottom}
      </div> -->
    </figure>

    <article id="narrative" class={$mq.classNames}>
      {#each textdata.story as scene, i}
        <div class="step-wrapper {$mq.classNames}">
          <div
            class="step {$mq.classNames}"
            step-index={i}
            scene={scene.scene}
            class:actionStep={scene.actions}
            class:annotationStep={scene.annotations}
            class:introStep={scene.scene == "intro"}
            class:lastStep={i == textdata.story.length - 1}
          >
            <div class="step-content {scene.boldText}">
              {@html formatText(scene.text)}
              {#if scene.scene == "intro"}
                <h1>{@html formatText(textdata.title)}</h1>
                <h4>{@html formatText(textdata.subtitle)}</h4>
                <BounceArrow />
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </article>
  </section>
</div>

<style>
  /* #debugger {
    position: absolute;
    top: 5px;
    left: 5px;
  } */

  .voila-credits {
    position: relative;
    display: block; 
    margin-left: 1rem;
    margin-right: 1rem;
    font-size: var(--voila-annotations-font-size);
    color: var(--lilac-medium);
  }

  #infra-story {
    margin: 0;
    background: var(--voila-bg);
    /* background-image: linear-gradient(to bottom right, var(--lilac-light), var(--lilac-light), var(--purple)); */
  }
  .scrolly {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
  .scrolly.mq-laptop {
    max-width: 1800px;
    flex-direction: row-reverse;
    justify-content: center;
    align-items: stretch;
    margin: 0 auto;
  }
  article#narrative {
    position: relative;
    width: 100%;
    min-width: 250px;
    z-index: var(--z-top);
    font-size: var(--voila-base-font-size-tablet);
    padding: 0 15px;
  }
  article#narrative.mq-laptop {
    width: 30%;
    flex-shrink: 3;
    font-size: var(--voila-base-font-size);
  }

  article#narrative .step-wrapper:first-child {
    padding-top: var(--firstMarginTop);
    margin-top: -100vh; /* For compact (mobile) view -- move steps up into viewport */
    /* margin-top: calc(var(--vh) * -100); */
  }

  article#narrative.mq-laptop .step-wrapper:first-child {
    margin-top: 0;
  }

  article#narrative .step {
    position: relative;
    min-height: var(--minHeight);
    max-height: var(--maxHeight);
    height: 100vh;
    /* height: calc(var(--vh, 1vh) * 100); */
    /* display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column; */
  }

  article#narrative .step.actionStep,
  article#narrative .step.annotationStep {
    max-height: none;
    max-height: calc(var(--maxHeight) * 2);
    height: 149vh;
    /* height: calc(var(--vh, 1vh) * 149); */
  }
  article#narrative.mq-mobile .step.actionStep,
  article#narrative .step.lastStep {
    height: 199vh;
    /* height: calc(var(--vh, 1vh) * 199); */
  }

  .step > .step-content {
    display: block;
    /* outline: 1px solid var(--off-black); */
    color: var(--off-black);
    background-color: rgba(255, 255, 255, 0.9);
    margin-left: auto;
    margin-right: auto;
    padding: 10px 25px;
    width: 100%;
    max-width: 350px;
    position: relative;
  }
  .step.introStep.mq-laptop > .step-content {
    background: none;
  }

  figure {
    height: 100vh;
    /* height: calc(var(--vh, 1vh) * 100); */
    min-height: var(--minHeight);
    position: -webkit-sticky;
    position: sticky;
    left: 0;
    width: 100%;
    margin: 0;
    z-index: var(--z-middle);
  }

  figure.mq-laptop {
    width: 70%;
  }

  .outro-text {
    position: absolute;
    opacity: 0;
    bottom: 10px;
    left: 10px;
    transition: opacity 0.2s;
    background-color: hsla(var(--off-white_hsl), 0.8);
    padding-left: 10px;
    padding-right: 10px;
    z-index: var(--z-overlay);
  }

  .outro-text.active {
    opacity: 1;
    transition: opacity 1s;
  }
</style>
